import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { fetch as apiFetch } from '../../utils/api-wrapper';
import pdfToImage from '../../utils/pdf-to-img';
import '../../components/receipt-preview.scss';

/** Preview a statement through its authorized document endpoint, not the invoice API. */
const StatementFilePreview = ({ statement, onOpen }) => {
  const [thumbnail, setThumbnail] = useState(null);
  const elementRef = useRef(null);
  const processing = statement.status === 'processing';

  useEffect(() => {
    setThumbnail(null);
    if (processing || !statement.id) return undefined;
    let active = true;
    let requested = false;
    const loadPreview = () => {
      if (requested) return;
      requested = true;
      apiFetch(`/c_card/statement-reconciliations/${statement.id}/document`, 'GET', {},
        async (response) => {
          const document = response?.data;
          if (!active || !document?.url || document.content_type !== 'application/pdf') return;
          try {
            const preview = await pdfToImage(document.url);
            if (active) setThumbnail(preview);
          } catch {
            // Keep the document icon and filename available if thumbnail rendering fails.
          }
        }, () => {});
    };
    // Only download PDF previews near the viewport, rather than every import at once.
    let observer;
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadPreview();
          observer.disconnect();
        }
      }, { rootMargin: '150px' });
      observer.observe(elementRef.current);
    } else {
      loadPreview();
    }
    return () => { active = false; observer?.disconnect(); };
  }, [statement.id, processing]);

  return (
    <button ref={elementRef} type='button' className='matched-receipt-preview statement-file-preview'
      disabled={processing} aria-label={statement.filename || '-'}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => { event.stopPropagation(); onOpen(); }}>
      <span className='matched-receipt-preview__thumbnail'>
        {thumbnail ? <img src={thumbnail} alt={statement.filename || ''} />
          : <FontAwesomeIcon icon={faFileAlt} aria-hidden='true' />}
      </span>
      <span className='matched-receipt-preview__filename' title={statement.filename || ''}>
        {statement.filename || '—'}
      </span>
    </button>
  );
};

export default StatementFilePreview;
